/**
 * Dashboard Page
 *
 * Main application page that composes all layout components.
 * Handles filtering, CRUD operations, and modal management.
 */

import { useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { QuickAddBar } from '@/components/layout/QuickAddBar';
import { CategoryTabs } from '@/components/layout/CategoryTabs';
import { SearchBar } from '@/components/layout/SearchBar';
import { CardGrid } from '@/components/CardGrid';
import { NotesJournal } from '@/components/notes';
import { AIPanel } from '@/components/ai/AIPanel';
import { DropZone } from '@/components/DropZone';
import { GettingStartedChecklist } from '@/components/onboarding';
import { KnowledgeTab } from '@/components/knowledge';
import { useApp } from '@/context/AppContext';
import { useData } from '@/context/DataContext';
import { useToast } from '@/components/ui/toast';
import { onRemoteChange } from '@/services/syncChannel';
import { filterItems, sortByUpdated } from '@/utils/search';
import { cn } from '@/utils/cn';
import type { Item, ItemType, TaskItem } from '@/types';

// ============================================
// DASHBOARD
// ============================================

type WorkspaceTab = 'items' | 'knowledge';

export function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab: WorkspaceTab = rawTab === 'knowledge' ? 'knowledge' : 'items';

  const setActiveTab = useCallback(
    (tab: WorkspaceTab) => {
      setSearchParams(tab === 'items' ? {} : { tab }, { replace: true });
    },
    [setSearchParams]
  );

  const { category, searchQuery, activeTag, view, openModal, setActiveTag } = useApp();

  const { config, allItems, isLoading, error, silentRefresh, updateItem, updateOnboarding } =
    useData();

  // Onboarding state
  const onboarding = config.onboarding;
  const showChecklist = !!onboarding?.completedWelcome && !onboarding?.dismissedChecklist;

  const handleChecklistDismiss = useCallback(() => {
    updateOnboarding({ dismissedChecklist: true });
  }, [updateOnboarding]);

  const { error: showError } = useToast();

  // Filter and sort items
  const filteredItems = sortByUpdated(
    filterItems(allItems, {
      category,
      query: searchQuery,
      tag: activeTag ?? undefined,
    })
  );

  // Handle background sync when tab becomes visible (works better on mobile)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void silentRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [silentRefresh]);

  // Listen for changes from other browser tabs (via BroadcastChannel)
  useEffect(() => {
    return onRemoteChange(() => {
      void silentRefresh();
    });
  }, [silentRefresh]);

  // Periodic polling for cross-device sync (every 15 seconds when visible)
  useEffect(() => {
    const startPolling = () => {
      return setInterval(() => {
        if (document.visibilityState === 'visible') {
          void silentRefresh();
        }
      }, 15000);
    };

    const interval = startPolling();
    return () => clearInterval(interval);
  }, [silentRefresh]);

  // Show error if data loading fails
  useEffect(() => {
    if (error) {
      showError('Error', error);
    }
  }, [error, showError]);

  // Handler for viewing an item
  const handleViewItem = useCallback(
    (item: Item) => {
      openModal({ mode: 'view', item });
    },
    [openModal]
  );

  // Handler for editing an item
  const handleEditItem = useCallback(
    (item: Item) => {
      openModal({ mode: 'edit', item });
    },
    [openModal]
  );

  // Handler for deleting an item
  const handleDeleteItem = useCallback(
    (item: Item) => {
      openModal({ mode: 'delete', item });
    },
    [openModal]
  );

  // Handler for toggling favorite
  const handleToggleFavorite = useCallback(
    (item: Item) => {
      updateItem({ ...item, isFavorite: !item.isFavorite }, { skipTimestampUpdate: true });
    },
    [updateItem]
  );

  // Handler for tag clicks
  const handleTagClick = useCallback(
    (tag: string) => {
      setActiveTag(activeTag === tag ? null : tag);
    },
    [activeTag, setActiveTag]
  );

  // Handler for toggling task completion
  const handleToggleComplete = useCallback(
    (item: TaskItem, completed: boolean) => {
      updateItem({ ...item, isCompleted: completed }, { skipTimestampUpdate: true });
    },
    [updateItem]
  );

  // Handler for adding new item
  const handleAddClick = useCallback(() => {
    // Pass current category as itemType if it's a valid item type
    const validTypes: ItemType[] = ['link', 'podcast', 'note', 'idea', 'file', 'task'];
    const itemType = validTypes.includes(category as ItemType) ? (category as ItemType) : undefined;
    openModal({ mode: 'add', itemType, prefillTags: ['inbox'] });
  }, [openModal, category]);

  return (
    <DropZone>
      <div className="min-h-screen bg-[var(--bg-primary)]">
        {/* Header */}
        <Header />

        {/* Workspace Tab Bar: Items | Knowledge */}
        <div className="bg-[var(--bg-secondary)] border-b border-[var(--border-primary)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex gap-1 py-1" role="tablist" aria-label="Workspace">
              {(['items', 'knowledge'] as const).map((tab) => (
                <button
                  key={tab}
                  role="tab"
                  aria-selected={activeTab === tab}
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                    activeTab === tab
                      ? 'bg-[var(--bg-primary)] text-[var(--text-primary)] shadow-sm'
                      : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                  )}
                >
                  {tab === 'items' ? 'Items' : 'Knowledge'}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {activeTab === 'knowledge' ? (
          <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <KnowledgeTab />
          </main>
        ) : (
          <>
            {/* Quick Add Bar */}
            <QuickAddBar />

            {/* Category Tabs */}
            <CategoryTabs />

            {/* Search Bar */}
            <SearchBar />

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              {showChecklist && <GettingStartedChecklist onDismiss={handleChecklistDismiss} />}

              {category === 'note' ? (
                <NotesJournal />
              ) : (
                <CardGrid
                  items={filteredItems}
                  view={view}
                  onItemClick={handleViewItem}
                  onEditItem={handleEditItem}
                  onDeleteItem={handleDeleteItem}
                  onToggleFavorite={handleToggleFavorite}
                  onTagClick={handleTagClick}
                  onToggleComplete={handleToggleComplete}
                  category={category}
                  searchQuery={searchQuery}
                  onAddClick={handleAddClick}
                  isLoading={isLoading}
                />
              )}
            </main>
          </>
        )}

        {/* AI Panel */}
        <AIPanel />
      </div>
    </DropZone>
  );
}
